import { useEffect, useState } from 'react';
import type { AriPushRequest } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { AdvancedRestrictions } from './AdvancedRestrictions';
import { AriOwnershipImpactDialog } from './AriOwnershipImpactDialog';
import { BasicAriFields } from './BasicAriFields';
import {
  buildAriPushRequest,
  buildInitialForm,
  emptyAriForm,
  hasAnythingToPush,
  snapDateRange,
  type AriInitialValues,
} from './helpers';
import { useAriPush } from './useAriPush';

interface AriEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  currency: string;
  /**
   * Optionale Prefill-Werte — Click auf Kalender-Tag lädt die bereits gepushten Rate +
   * Restrictions für diesen Tag, damit der User sieht was aktuell aktiv ist und gezielt
   * editieren kann statt einen leeren Form zu sehen.
   */
  initial?: AriInitialValues;
}

export function AriEditModal({
  open,
  onOpenChange,
  propertyId,
  currency,
  initial,
}: AriEditModalProps) {
  const [form, setForm] = useState(emptyAriForm);
  const [confirmation, setConfirmation] = useState<AriPushRequest | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? buildInitialForm(initial) : emptyAriForm());
    }
  }, [open, initial]);

  const push = useAriPush({
    onSuccess: () => {
      setConfirmation(null);
      onOpenChange(false);
    },
  });

  const nothingToPush = !hasAnythingToPush(form);

  return (
    <>
      <Dialog
        open={open && confirmation === null}
        onOpenChange={(value) => {
          if (!value) setConfirmation(null);
          onOpenChange(value);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preise & Verfügbarkeit</DialogTitle>
            <DialogDescription>
              Push zu Channex — von dort verteilt an alle verbundenen OTAs. Max 90 Tage pro Aktion.
              Vergangene Daten nicht erlaubt.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <BasicAriFields
              form={form}
              currency={currency}
              currentAvailable={initial?.available}
              onChange={(field, value) =>
                setForm((f) =>
                  field === 'from' || field === 'to'
                    ? {
                        ...f,
                        ...snapDateRange(
                          field === 'from' ? value : f.from,
                          field === 'to' ? value : f.to,
                          field,
                        ),
                      }
                    : { ...f, [field]: value },
                )
              }
            />

            <AdvancedRestrictions
              form={form}
              current={initial?.currentRestrictions}
              onChange={(field, value) => setForm((f) => ({ ...f, [field]: value }))}
            />

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                variant="brand"
                loading={push.isPending}
                disabled={nothingToPush}
                onClick={() => setConfirmation(buildAriPushRequest(propertyId, form))}
              >
                An Channex pushen
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <AriOwnershipImpactDialog
        open={open && confirmation !== null}
        propertyId={propertyId}
        ranges={confirmation ? [confirmation] : []}
        saving={push.isPending}
        onOpenChange={(value) => !value && setConfirmation(null)}
        onConfirm={() => confirmation && push.mutate(confirmation)}
      />
    </>
  );
}
